import type { PrivyClientConfig } from "@privy-io/react-auth";
import { polygon, polygonAmoy, addRpcUrlOverrideToChain } from "@privy-io/chains";
import { getActiveChainId, POLYGON_CHAIN_ID } from "./activeChain";
import { resolveAmoyRpcUrl } from "./rpcUrls";
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from "@/lib/constants";

export const privyAppId = import.meta.env.VITE_PRIVY_APP_ID as string | undefined;

export const useMockAuth = import.meta.env.VITE_USE_MOCK_AUTH === "true" || !privyAppId;

// Follows the admin's network switch when one is set (see activeChain.ts);
// otherwise VITE_POLYGON_CHAIN_ID. Fixed for the page load -- switching reloads.
const chainId = getActiveChainId();

// Override Privy's default RPC with a dedicated endpoint to avoid rate limits.
// Set VITE_POLYGON_RPC_URL to your Alchemy/QuickNode endpoint for best results.
// Only resolved on Amoy — on mainnet, Privy uses its own RPC or VITE_POLYGON_MAINNET_RPC_URL.
const customAmoyRpcUrl =
  chainId !== POLYGON_CHAIN_ID
    ? resolveAmoyRpcUrl(import.meta.env.VITE_POLYGON_RPC_URL as string | undefined)
    : undefined;

const amoyChain = customAmoyRpcUrl
  ? addRpcUrlOverrideToChain(polygonAmoy, customAmoyRpcUrl)
  : polygonAmoy;

const polygonChain = import.meta.env.VITE_POLYGON_MAINNET_RPC_URL
  ? addRpcUrlOverrideToChain(polygon, import.meta.env.VITE_POLYGON_MAINNET_RPC_URL as string)
  : polygon;

/**
 * The only two strings in the Privy modal we control. Privy exposes no
 * `locale` option, so the rest of its chrome ("Continue with SMS", "Submit",
 * error text) renders in English regardless of the app's language.
 */
export interface PrivyLoginCopy {
  landingHeader: string;
  loginMessage: string;
}

/**
 * English copy, used when no translation is supplied. Mirrors the
 * `auth.loginTitle` / `auth.loginSubtitle` locale keys and exists so this
 * module stays self-contained — callers inside the LanguageProvider tree
 * should pass translated copy instead (see AuthProvider in main.tsx).
 */
const DEFAULT_LOGIN_COPY: PrivyLoginCopy = {
  landingHeader: "Welcome to weXare",
  loginMessage: "Sign in to create quests and earn bounties. You must be 18 or older to use weXare.",
};

export function getPrivyConfig(
  theme: "light" | "dark",
  copy: PrivyLoginCopy = DEFAULT_LOGIN_COPY,
): PrivyClientConfig {
  return {
    appearance: {
      theme,
      accentColor: "#6366f1",
      showWalletLoginFirst: false,
      landingHeader: copy.landingHeader,
      loginMessage: copy.loginMessage,
    },
    // Privy renders these as "By logging in I agree to the Terms and Privacy
    // Policy" in the modal footer. That footer is English-only, which is why the
    // 18+ requirement rides in the (translated) loginMessage instead.
    legal: {
      termsAndConditionsUrl: TERMS_OF_SERVICE_URL,
      privacyPolicyUrl: PRIVACY_POLICY_URL,
    },
    // "sms" surfaces Privy's phone-number login (DEV-767). Like every entry
    // here it only *filters* what the modal shows — SMS must also be enabled
    // in the Privy dashboard for the button to work.
    loginMethods: ["email", "google", "sms"],
    embeddedWallets: {
      ethereum: {
        createOnLogin: "users-without-wallets",
      },
      showWalletUIs: false,
    },
    supportedChains: [amoyChain, polygonChain],
    defaultChain: chainId === POLYGON_CHAIN_ID ? polygonChain : amoyChain,
  };
}
